import { useState, useEffect, useCallback } from "react";
import {
  Loader2,
  AlertCircle,
  DollarSign,
  Eye,
  MousePointer,
  GripVertical,
} from "lucide-react";
import { colors, fonts, PIPELINE_STAGES } from "../styles/theme";
import { useAuth } from "../hooks/useAuth";
import { useTenant } from "../contexts/TenantContext";
import { fetchPipeline, movePipelineStage, updateContact, fmtDate } from "../services/api";
import BookSales from "../components/BookSales";

export default function Pipeline() {
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState({});
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { role } = useAuth();
  const { formatCurrency, currencySymbol } = useTenant();
  const [activeTab, setActiveTab] = useState("advisory");

  // Drag state
  const [dragDeal, setDragDeal] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [moving, setMoving] = useState(null); // contactId being moved

  // Inline editing state: { dealId, field: "dealValue"|"dealProbability" }
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetchPipeline({ range: 90 })
      .then((data) => {
        setDeals(data.deals || []);
        setStats(data.stats || {});
        setRecentPayments(data.recentPayments || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleDragStart = useCallback((e, deal) => {
    setDragDeal(deal);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", deal.id);
    // Make the drag image slightly transparent
    if (e.currentTarget) {
      e.currentTarget.style.opacity = "0.5";
    }
  }, []);

  const handleDragEnd = useCallback((e) => {
    if (e.currentTarget) {
      e.currentTarget.style.opacity = "1";
    }
    setDragDeal(null);
    setDropTarget(null);
  }, []);

  const handleDragOver = useCallback((e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget(stageId);
  }, []);

  const handleDragLeave = useCallback((e) => {
    // Only clear if leaving the column itself
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDropTarget(null);
    }
  }, []);

  const handleDrop = useCallback(
    async (e, toStage) => {
      e.preventDefault();
      setDropTarget(null);

      if (!dragDeal || dragDeal.stage === toStage) {
        setDragDeal(null);
        return;
      }

      const deal = dragDeal;
      setDragDeal(null);
      setMoving(deal.id);

      // Optimistically move the deal in the UI
      setDeals((prev) =>
        prev.map((d) => (d.id === deal.id ? { ...d, stage: toStage } : d)),
      );

      try {
        await movePipelineStage(deal.id, deal.email, toStage);
      } catch (err) {
        // Revert on failure
        setDeals((prev) =>
          prev.map((d) =>
            d.id === deal.id ? { ...d, stage: deal.stage } : d,
          ),
        );
        alert("Failed to move contact: " + err.message);
      } finally {
        setMoving(null);
      }
    },
    [dragDeal],
  );

  const handleInlineSave = useCallback(async (dealId, field, rawValue) => {
    setEditing(null);
    const value = rawValue === "" ? null : parseFloat(rawValue);
    if (isNaN(value) && rawValue !== "") return;

    // Find current deal
    const deal = deals.find((d) => d.id === dealId);
    if (!deal) return;

    const currentVal = field === "dealValue" ? deal.dealValue : deal.probability;
    if (value === currentVal) return;

    // Optimistically update
    setDeals((prev) =>
      prev.map((d) => {
        if (d.id !== dealId) return d;
        const stage = PIPELINE_STAGES.find((s) => s.id === d.stage);
        const newDealValue = field === "dealValue" ? (value || 0) : d.dealValue;
        const newProb = field === "dealProbability"
          ? (value ?? stage?.defaultProbability ?? 0)
          : d.probability;
        return {
          ...d,
          dealValue: newDealValue,
          probability: newProb,
          hasCustomProbability: field === "dealProbability" ? value != null : d.hasCustomProbability,
          weightedValue: Math.round(newDealValue * newProb / 100),
        };
      }),
    );

    try {
      await updateContact(dealId, { [field]: value });
    } catch (err) {
      // Revert — re-fetch
      const data = await fetchPipeline({ range: 90 });
      setDeals(data.deals || []);
    }
  }, [deals]);

  const activeDeals = deals.filter(
    (d) => d.stage !== "closed_won" && d.stage !== "closed_lost",
  );

  return (
    <div>
      {/* Tab Switcher */}
      <div
        style={{
          display: "flex",
          gap: 0,
          marginBottom: 24,
          borderBottom: `2px solid ${colors.border}`,
        }}
      >
        {[
          { id: "advisory", label: "Advisory Pipeline" },
          { id: "book_sales", label: "Book Sales" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: fonts.body,
              border: "none",
              borderBottom: `2px solid ${activeTab === tab.id ? colors.accent : "transparent"}`,
              marginBottom: -2,
              background: "none",
              color: activeTab === tab.id ? colors.text : colors.textSecondary,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "book_sales" && <BookSales />}

      {activeTab === "advisory" && loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "60vh",
          }}
        >
          <Loader2
            size={24}
            color={colors.accent}
            className="spin"
          />
        </div>
      )}

      {activeTab === "advisory" && error && !loading && (
        <div style={{ padding: 32, textAlign: "center", color: colors.danger }}>
          <AlertCircle size={24} style={{ marginBottom: 8 }} />
          <div>{error}</div>
        </div>
      )}

      {activeTab === "advisory" && !loading && !error && (<>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 28,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: 28,
              fontWeight: 700,
              color: colors.text,
              marginBottom: 4,
            }}
          >
            Pipeline
          </h1>
          <p
            style={{
              fontFamily: fonts.body,
              fontSize: 14,
              color: colors.textSecondary,
            }}
          >
            {activeDeals.length} active contacts · {deals.length} total
            {stats.totalPipelineValue > 0 &&
              ` · ${formatCurrency(stats.totalPipelineValue)} pipeline`}
            {stats.weightedPipeline > 0 &&
              ` · ${formatCurrency(stats.weightedPipeline)} weighted`}
            {role !== "sales" && stats.totalRevenue > 0 &&
              ` · ${formatCurrency(stats.totalRevenue)} revenue (${stats.rangeDays}d)`}
          </p>
        </div>
      </div>

      {/* Kanban Board */}
      <div
        style={{
          display: "flex",
          gap: 12,
          overflowX: "auto",
          paddingBottom: 16,
        }}
      >
        {PIPELINE_STAGES.filter((s) => s.id !== "closed_lost").map((stage) => {
          const stageDeals = deals.filter((d) => d.stage === stage.id);
          const stageValue = stageDeals.reduce((s, d) => s + (d.dealValue || 0), 0);
          const stageWeighted = stageDeals.reduce((s, d) => s + (d.weightedValue || 0), 0);
          const isOver = dropTarget === stage.id && dragDeal && dragDeal.stage !== stage.id;
          return (
            <div
              key={stage.id}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={(e) => handleDragLeave(e, stage.id)}
              onDrop={(e) => handleDrop(e, stage.id)}
              style={{
                minWidth: 260,
                flex: 1,
                background: isOver ? colors.accentDim : colors.bgCard,
                borderRadius: 8,
                border: `2px solid ${isOver ? colors.accent : colors.border}`,
                display: "flex",
                flexDirection: "column",
                transition: "background 0.15s, border-color 0.15s",
              }}
            >
              {/* Stage Header */}
              <div
                style={{
                  padding: "14px 16px",
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: stage.color,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      fontFamily: fonts.body,
                      color: colors.text,
                    }}
                  >
                    {stage.label}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textSecondary,
                      background: "rgba(0,0,0,0.04)",
                      padding: "1px 6px",
                      borderRadius: 4,
                    }}
                  >
                    {stageDeals.length}
                  </span>
                </div>
                {stageValue > 0 && (
                  <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4, paddingLeft: 16 }}>
                    {formatCurrency(stageValue)}
                    {stageWeighted !== stageValue && (
                      <span style={{ color: colors.textSecondary }}> · {formatCurrency(stageWeighted)} wtd</span>
                    )}
                  </div>
                )}
              </div>

              {/* Deal Cards */}
              <div
                style={{
                  padding: 8,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  flex: 1,
                  maxHeight: 480,
                  overflowY: "auto",
                }}
              >
                {stageDeals.map((deal) => (
                  <div
                    key={deal.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, deal)}
                    onDragEnd={handleDragEnd}
                    style={{
                      padding: 14,
                      background: colors.bg,
                      borderRadius: 6,
                      border: `1px solid ${colors.border}`,
                      cursor: moving === deal.id ? "wait" : "grab",
                      transition: "border-color 0.15s, opacity 0.15s",
                      opacity: moving === deal.id ? 0.6 : 1,
                      position: "relative",
                    }}
                    onMouseEnter={(e) => {
                      if (moving !== deal.id) {
                        e.currentTarget.style.borderColor = colors.accent;
                      }
                    }}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.borderColor = colors.border)
                    }
                  >
                    {/* Drag handle indicator */}
                    <div
                      style={{
                        position: "absolute",
                        top: 14,
                        right: 8,
                        color: colors.textSecondary,
                        opacity: 0.4,
                      }}
                    >
                      <GripVertical size={12} />
                    </div>

                    {/* Name */}
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: fonts.body,
                        color: colors.text,
                        marginBottom: 4,
                        paddingRight: 20,
                      }}
                    >
                      {deal.name}
                      {moving === deal.id && (
                        <Loader2
                          size={11}
                          color={colors.accent}
                          className="spin"
                          style={{
                            marginLeft: 6,
                            verticalAlign: "middle",
                          }}
                        />
                      )}
                    </div>

                    {/* Company + Email */}
                    <div
                      style={{
                        fontSize: 12,
                        color: colors.textSecondary,
                        marginBottom: 6,
                      }}
                    >
                      {deal.company && <span>{deal.company}</span>}
                      {deal.companyContactCount > 1 && (
                        <span
                          style={{
                            fontSize: 10,
                            color: colors.textSecondary,
                            marginLeft: 4,
                          }}
                        >
                          ({deal.companyContactCount} contacts)
                        </span>
                      )}
                      {deal.company && " · "}
                      <span style={{ color: colors.textSecondary }}>
                        {deal.email}
                      </span>
                    </div>

                    {/* Deal value + probability */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      {editing?.dealId === deal.id && editing?.field === "dealValue" ? (
                        <input
                          autoFocus
                          type="number"
                          min="0"
                          step="1000"
                          defaultValue={deal.dealValue || ""}
                          placeholder="0"
                          style={{
                            width: 80, fontSize: 12, fontFamily: fonts.mono,
                            padding: "2px 4px", border: `1px solid ${colors.accent}`,
                            borderRadius: 3, outline: "none",
                          }}
                          onBlur={(e) => handleInlineSave(deal.id, "dealValue", e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.target.blur();
                            if (e.key === "Escape") setEditing(null);
                          }}
                        />
                      ) : (
                        <span
                          onClick={(e) => { e.stopPropagation(); setEditing({ dealId: deal.id, field: "dealValue" }); }}
                          style={{
                            fontSize: 12, fontFamily: fonts.mono, cursor: "pointer",
                            color: deal.dealValue ? colors.text : colors.textSecondary,
                            padding: "1px 4px", borderRadius: 3,
                            border: `1px dashed ${deal.dealValue ? "transparent" : colors.border}`,
                          }}
                          title="Click to set deal value"
                        >
                          {deal.dealValue ? formatCurrency(deal.dealValue) : `${currencySymbol()}—`}
                        </span>
                      )}
                      <span style={{ fontSize: 10, color: colors.textSecondary }}>·</span>
                      {editing?.dealId === deal.id && editing?.field === "dealProbability" ? (
                        <input
                          autoFocus
                          type="number"
                          min="0"
                          max="100"
                          defaultValue={deal.hasCustomProbability ? deal.probability : ""}
                          placeholder={`${stage.defaultProbability}%`}
                          style={{
                            width: 50, fontSize: 12, fontFamily: fonts.mono,
                            padding: "2px 4px", border: `1px solid ${colors.accent}`,
                            borderRadius: 3, outline: "none",
                          }}
                          onBlur={(e) => handleInlineSave(deal.id, "dealProbability", e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.target.blur();
                            if (e.key === "Escape") setEditing(null);
                          }}
                        />
                      ) : (
                        <span
                          onClick={(e) => { e.stopPropagation(); setEditing({ dealId: deal.id, field: "dealProbability" }); }}
                          style={{
                            fontSize: 11, fontFamily: fonts.mono, cursor: "pointer",
                            color: deal.hasCustomProbability ? colors.accent : colors.textSecondary,
                            padding: "1px 4px", borderRadius: 3,
                          }}
                          title="Click to set probability"
                        >
                          {deal.probability}%
                        </span>
                      )}
                      {deal.dealValue > 0 && deal.weightedValue > 0 && (
                        <span style={{ fontSize: 10, color: colors.textSecondary, fontFamily: fonts.mono }}>
                          = {formatCurrency(deal.weightedValue)}
                        </span>
                      )}
                    </div>

                    {/* Engagement mini-stats */}
                    {(deal.engagement.opens > 0 ||
                      deal.engagement.clicks > 0) && (
                      <div
                        style={{ display: "flex", gap: 10, marginBottom: 6 }}
                      >
                        {deal.engagement.opens > 0 && (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                              fontSize: 11,
                              fontFamily: fonts.mono,
                              color: colors.success,
                            }}
                          >
                            <Eye size={10} />
                            {deal.engagement.opens}
                          </span>
                        )}
                        {deal.engagement.clicks > 0 && (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                              fontSize: 11,
                              fontFamily: fonts.mono,
                              color: colors.accent,
                            }}
                          >
                            <MousePointer size={10} />
                            {deal.engagement.clicks}
                          </span>
                        )}
                        {deal.engagement.openRate > 0 && (
                          <span
                            style={{ fontSize: 10, color: colors.textSecondary }}
                          >
                            {deal.engagement.openRate.toFixed(0)}% open
                          </span>
                        )}
                      </div>
                    )}

                    {/* Tags */}
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {(deal.tags || []).slice(0, 3).map((t) => (
                        <span
                          key={t}
                          style={{
                            fontSize: 9,
                            padding: "1px 5px",
                            borderRadius: 3,
                            fontFamily: fonts.mono,
                            background: "rgba(0,0,0,0.04)",
                            color: colors.textSecondary,
                            border: `1px solid ${colors.border}`,
                          }}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                {stageDeals.length === 0 && (
                  <div
                    style={{
                      padding: 24,
                      textAlign: "center",
                      color: colors.textSecondary,
                      fontSize: 12,
                      border: `1px dashed ${colors.border}`,
                      borderRadius: 6,
                    }}
                  >
                    {isOver ? "Drop here" : "No contacts"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Stage Summary */}
      <div
        style={{
          marginTop: 20,
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        {PIPELINE_STAGES.filter((s) => s.id !== "closed_lost").map((stage) => {
          const stDeals = deals.filter((d) => d.stage === stage.id);
          const stValue = stDeals.reduce((s, d) => s + (d.dealValue || 0), 0);
          const stWeighted = stDeals.reduce((s, d) => s + (d.weightedValue || 0), 0);
          return (
            <div
              key={stage.id}
              style={{
                flex: 1,
                minWidth: 120,
                padding: "16px 20px",
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  fontFamily: fonts.heading,
                  color: stage.color,
                }}
              >
                {stDeals.length}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: colors.textSecondary,
                  fontFamily: fonts.body,
                }}
              >
                {stage.label}
              </div>
              {stValue > 0 && (
                <div style={{ fontSize: 11, fontFamily: fonts.mono, color: colors.textSecondary, marginTop: 4 }}>
                  {formatCurrency(stValue)}
                  {stWeighted !== stValue && ` · ${formatCurrency(stWeighted)} wtd`}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Recent Payments */}
      {role !== "sales" && recentPayments.length > 0 && (
        <div
          style={{
            marginTop: 24,
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: 24,
          }}
        >
          <h3
            style={{
              fontFamily: fonts.heading,
              fontSize: 16,
              color: colors.text,
              marginBottom: 16,
            }}
          >
            Recent Payments
          </h3>
          {recentPayments.map((p) => (
            <div
              key={p.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 0",
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <DollarSign size={14} color={colors.success} />
                <div>
                  <div style={{ fontSize: 13, color: colors.text }}>
                    {p.description || p.customerEmail || "Payment"}
                  </div>
                  {p.customerEmail && p.description && (
                    <div style={{ fontSize: 11, color: colors.textSecondary }}>
                      {p.customerEmail}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: 14,
                    fontFamily: fonts.mono,
                    fontWeight: 700,
                    color: colors.success,
                  }}
                >
                  {formatCurrency(p.amount)}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: colors.textSecondary,
                    fontFamily: fonts.mono,
                  }}
                >
                  {fmtDate(p.date)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      </>)}    </div>
  );
}
